import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { isMemberProfileReady, isOnlyBirthdayMissing } from './memberEligibility'
import type { MemberWelcome } from './memberJoin'
import { ageText, buildSurveyPayload, GENDERS, surveyProgress, toggleAnswer, visibleQuestions, type SurveyAnswers, type SurveyConfig } from './joinSurvey'
import {
  currentMemberIdToken,
  fetchMemberProfile,
  saveMemberProfile,
  type MemberProfile,
} from '../lib/api/memberApi'
import { ThaiAddressInput } from '../components/form/ThaiAddressInput'
import { addressPartsOfText, isAddressTextReady } from '../lib/thaiAddressText'

const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม',
]
const DAYS = Array.from({ length: 31 }, (_, index) => String(index + 1))
const MONTHS = THAI_MONTHS
const YEARS = Array.from({ length: new Date().getFullYear() - 1899 }, (_, index) => String(new Date().getFullYear() + 543 - index))

type DraftProfile = {
  recipientName: string
  phone: string
  address: string
  birthdayDay: string
  birthdayMonth: string
  birthdayYear: string
  gender: string
}

const EMPTY: DraftProfile = {
  recipientName: '',
  phone: '',
  address: '',
  birthdayDay: '',
  birthdayMonth: '',
  birthdayYear: '',
  gender: '',
}

function birthdayToForm(birthday: string): Pick<DraftProfile, 'birthdayDay' | 'birthdayMonth' | 'birthdayYear'> {
  const [day = '', rawMonth = '', year = ''] = String(birthday || '').split('/')
  const monthNumber = Number(rawMonth)
  return {
    birthdayDay: DAYS.includes(day) ? day : '',
    birthdayMonth: Number.isInteger(monthNumber) && monthNumber >= 1 && monthNumber <= 12
      ? THAI_MONTHS[monthNumber - 1]
      : THAI_MONTHS.includes(rawMonth) ? rawMonth : '',
    birthdayYear: YEARS.includes(year) ? year : YEARS.includes(String(Number(year) + 543)) ? String(Number(year) + 543) : '',
  }
}

function profileToForm(profile?: MemberProfile): DraftProfile {
  if (!profile) return EMPTY
  return {
    recipientName: profile.recipientName,
    phone: profile.phone,
    address: profile.address,
    ...birthdayToForm(profile.birthday),
    gender: profile.gender || '',
  }
}

function formToProfile(form: DraftProfile): Omit<MemberProfile, 'needsSetup' | 'source' | 'survey'> {
  const unknown = (value: string) => !value || value === 'ไม่ทราบ'
  return {
    recipientName: form.recipientName,
    phone: form.phone,
    address: form.address,
    // ที่อยู่แยกช่อง (CF เก่าไม่รู้จัก = ข้ามไป ไม่ทำให้บันทึกล้ม)
    addressParts: addressPartsOfText(form.address),
    birthday: `${unknown(form.birthdayDay) ? 'ไม่ทราบ' : form.birthdayDay}/${unknown(form.birthdayMonth) ? 'ไม่ทราบ' : form.birthdayMonth}/${unknown(form.birthdayYear) ? 'ไม่ทราบ' : form.birthdayYear}`,
    // ไม่เลือก = ไม่ส่ง (CF คงค่าเดิม)
    ...(form.gender ? { gender: form.gender } : {}),
  }
}

/** โปรไฟล์สมาชิก 1 บัญชีต่อ 1 ชุด — แทนสมุดที่อยู่หลายรายการเดิม */
export function AddressBook({ initialProfile, onSaved, openRequest = 0, joinMode = false, onWelcome, hasOrders = false }: {
  initialProfile?: MemberProfile
  onSaved: (profile: MemberProfile) => void
  openRequest?: number
  /** มาจากลิงก์สมัคร (join=1) — หัวข้อ/ปุ่มเป็นการสมัครครอบครัวศรีคเนศ */
  joinMode?: boolean
  /** สมัครครั้งแรกสำเร็จ — เปิดพิธีต้อนรับ */
  onWelcome?: (welcome: MemberWelcome) => void
  /** มีประวัติออร์เดอร์ — ไม่ถามข้อ "เคยร่วมบูชาไหม" (ระบบรู้แล้ว) */
  hasOrders?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [profile, setProfile] = useState<MemberProfile | undefined>(initialProfile)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  // ส่วนที่ 2 แบบสำรวจ (ไม่บังคับ) — ค่าตั้งมาจาก memberApi · CF เก่าไม่ส่ง = ไม่แสดง
  const [surveyConfig, setSurveyConfig] = useState<SurveyConfig | null>(null)
  const [answers, setAnswers] = useState<SurveyAnswers>({})
  const [beliefConsent, setBeliefConsent] = useState(false)
  const promptedRef = useRef(false)
  const [form, setForm] = useState<DraftProfile>(() => profileToForm(initialProfile))
  const sheetRef = useRef<HTMLElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => { if (openRequest > 0) setOpen(true) }, [openRequest])
  useEffect(() => {
    if (initialProfile && !isMemberProfileReady(initialProfile) && !promptedRef.current) {
      promptedRef.current = true
      setOpen(true)
    }
  }, [initialProfile])

  useEffect(() => {
    if (!open) return
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setOpen(false); return }
      if (event.key !== 'Tab') return
      const controls = sheetRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), input, textarea, select')
      if (!controls?.length) return
      if (event.shiftKey && document.activeElement === controls[0]) { event.preventDefault(); controls[controls.length - 1].focus() }
      else if (!event.shiftKey && document.activeElement === controls[controls.length - 1]) { event.preventDefault(); controls[0].focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
      if (previousFocus?.isConnected && previousFocus !== document.body) previousFocus.focus()
      else document.querySelector<HTMLButtonElement>('.mo-profile-edit')?.focus()
    }
  }, [open])

  useEffect(() => {
    setProfile(initialProfile)
    if (initialProfile) setForm(profileToForm(initialProfile))
  }, [initialProfile])

  useEffect(() => {
    if (!open) return
    let active = true
    setLoading(true)
    setError('')
    setConfirmed(false)
    void (async () => {
      try {
        const token = await currentMemberIdToken()
        if (!token) throw new Error('กรุณาเปิดจาก LINE อีกครั้ง')
        const response = await fetchMemberProfile(token)
        if (!response.data.ok || !response.data.profile) throw new Error(response.data.message || 'โหลดข้อมูลสมาชิกไม่สำเร็จ')
        if (active) {
          setProfile(response.data.profile)
          setForm(profileToForm(response.data.profile))
          setSurveyConfig(response.data.surveyConfig || null)
          setAnswers(response.data.profile.survey?.answers || {})
          setBeliefConsent(response.data.profile.survey?.beliefConsent === true)
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'โหลดข้อมูลสมาชิกไม่สำเร็จ')
      } finally {
        if (active) setLoading(false)
      }
    })()
    return () => { active = false }
  }, [open])

  const save = async () => {
    if (!confirmed) { setError('กรุณายืนยันว่าข้อมูลนี้เป็นข้อมูลปัจจุบันของคุณ'); return }
    if (!isAddressTextReady(form.address)) { setError('กรุณากรอกที่อยู่ให้ครบ (บ้านเลขที่ รหัสไปรษณีย์ จังหวัด อำเภอ ตำบล)'); return }
    setLoading(true)
    setError('')
    try {
      const token = await currentMemberIdToken()
      if (!token) throw new Error('กรุณาเปิดจาก LINE อีกครั้ง')
      const response = await saveMemberProfile(token, formToProfile(form), buildSurveyPayload(surveyConfig, answers, beliefConsent))
      if (!response.data.ok || !response.data.profile) throw new Error(response.data.message || 'บันทึกไม่สำเร็จ')
      setProfile(response.data.profile)
      setOpen(false)
      if (response.data.welcome?.first && onWelcome) onWelcome(response.data.welcome)
      onSaved(response.data.profile)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  const incomplete = !isMemberProfileReady(profile)
  const joining = joinMode && incomplete
  // สมัครไว้แล้วแต่ขาดวันเกิด — บอกให้ชัดว่าเหลืออีกขั้นเดียว แล้วค่อยต้อนรับ
  const lastStep = incomplete && isOnlyBirthdayMissing(profile)
  const questions = visibleQuestions(surveyConfig, hasOrders)
  const progress = surveyProgress(surveyConfig, answers, beliefConsent)
  const bonusOpen = !!surveyConfig && surveyConfig.bonusPoints > 0 && !profile?.survey?.bonusAwarded && progress.total > 0
  const age = ageText(form.birthdayDay, form.birthdayMonth, form.birthdayYear)
  return <>
    {incomplete && <button type="button" className="mo-glass mo-address-entry is-incomplete" onClick={() => setOpen(true)} aria-label="กรอกข้อมูลสมาชิกให้ครบ">
      <span className="mo-address-icon" aria-hidden="true"><i className="fas fa-id-card" /></span>
      {lastStep
        ? <span className="mo-address-copy"><b>เหลืออีกขั้นเดียว — เติมวันเกิด</b><small>เติมวันเกิดเพื่อสมัครสมาชิกให้สมบูรณ์ และเปิดรับสิทธิพิเศษ</small></span>
        : <span className="mo-address-copy"><b>ยืนยันข้อมูลเพื่อเปิดรับสิทธิพิเศษ</b><small>ยังรับสิทธิพิเศษไม่ได้ จนกว่าจะกรอกและยืนยันข้อมูลปัจจุบันครบ</small></span>}
      <span className="mo-address-chevron" aria-hidden="true">›</span>
    </button>}
    {open && createPortal(<div className="mo-address-backdrop" role="presentation" onClick={() => setOpen(false)}>
      <section ref={sheetRef} className="mo-address-sheet" role="dialog" aria-modal="true" aria-label="ข้อมูลพื้นฐานของฉัน" onClick={(event) => event.stopPropagation()}>
        <div className="mo-address-handle" />
        <div className="mo-address-head"><h2>{joining ? 'สมัครครอบครัวศรีคเนศ' : incomplete ? 'ยืนยันข้อมูลสมาชิก' : 'ข้อมูลพื้นฐานของฉัน'}</h2><button ref={closeRef} type="button" onClick={() => setOpen(false)} aria-label="ปิด">×</button></div>
        {/* หัวฟอร์ม 2 บรรทัด: ได้อะไร + ไม่ขอข้อมูลที่มิจฉาชีพมักขอ */}
        {incomplete && <p className="mo-profile-required mo-profile-join" role="note"><b>{lastStep ? 'เหลืออีกขั้นเดียว — เติมวันเกิด' : 'สมัครฟรี รับสิทธิ์สมาชิกทันที'}</b><span>ใช้เพียงข้อมูลจัดส่งรายการบูชา · ไม่ขอเลขบัตร รหัส OTP หรือเลขบัญชีใด ๆ</span></p>}
        {error && <p className="mo-address-error" role="alert">{error}</p>}
        {loading && !profile ? <p className="mo-address-intro" role="status">กำลังโหลด...</p> : <form className="mo-address-form" onSubmit={(event) => { event.preventDefault(); void save() }}>
          {questions.length > 0 && <div className="mo-join-part"><b>1 · ข้อมูลจัดส่ง</b><span className="mo-join-pill">จำเป็น</span></div>}
          <label>ชื่อผู้รับ<input required autoComplete="name" value={form.recipientName} maxLength={100} onChange={(event) => setForm({ ...form, recipientName: event.target.value })} /></label>
          <label>เบอร์โทรผู้รับ<input required autoComplete="tel" type="tel" inputMode="tel" value={form.phone} maxLength={15} onChange={(event) => setForm({ ...form, phone: event.target.value })} /></label>
          <fieldset className="mo-address-fieldset">
            <ThaiAddressInput id="memberAddressLine" variant="sheet" title="ที่อยู่จัดส่ง" value={form.address}
              onChange={(text) => setForm((prev) => ({ ...prev, address: text }))}
              lineProps={{ required: true, autoComplete: 'street-address' }} />
          </fieldset>
          <fieldset className="mo-address-birthday">
            <legend>วันเกิด</legend>
            <p>ระบุวัน เดือน ปีเกิดจริง (ปี พ.ศ.) เพื่อรับสิทธิ์วันเกิด</p>
            <div>
              <select required aria-label="วันเกิด" value={form.birthdayDay} onChange={(event) => setForm({ ...form, birthdayDay: event.target.value })}>
                <option value="">วัน</option>
                {DAYS.map((day) => <option key={day} value={day}>{day === 'ไม่ทราบ' ? 'ไม่ทราบวัน' : day}</option>)}
              </select>
              <select required aria-label="เดือนเกิด" value={form.birthdayMonth} onChange={(event) => setForm({ ...form, birthdayMonth: event.target.value })}>
                <option value="">เดือน</option>
                {MONTHS.map((month) => <option key={month} value={month}>{month === 'ไม่ทราบ' ? 'ไม่ทราบเดือน' : month}</option>)}
              </select>
              <select required aria-label="ปีเกิด" value={form.birthdayYear} onChange={(event) => setForm({ ...form, birthdayYear: event.target.value })}>
                <option value="">ปี พ.ศ.</option>
                {YEARS.map((year) => <option key={year} value={year}>{year === 'ไม่ทราบ' ? 'ไม่ทราบปี' : year}</option>)}
              </select>
            </div>
            {age && <small className="mo-join-age" aria-live="polite">{age}</small>}
          </fieldset>
          <fieldset className="mo-join-q">
            <legend>เพศ</legend>
            <div className="mo-join-chips" role="radiogroup" aria-label="เพศ">
              {GENDERS.map((g) => <button key={g} type="button" role="radio" aria-checked={form.gender === g} className="mo-join-chip" onClick={() => setForm({ ...form, gender: form.gender === g ? '' : g })}>{g}</button>)}
            </div>
          </fieldset>
          {questions.length > 0 && <>
            <div className="mo-join-part"><b>2 · เล่าให้เรารู้จักคุณ</b><span className="mo-join-pill is-opt">ไม่บังคับ</span><small>ข้ามได้</small></div>
            {hasOrders && surveyConfig?.questions.some((q) => q.id === 'history' && q.enabled) && <p className="mo-join-known">ขอบคุณที่ร่วมบูชากับศรีคเนศ ระบบจำประวัติไว้ให้แล้ว</p>}
            {questions.map((q) => (
              <fieldset key={q.id} className="mo-join-q">
                <legend>{q.label}{q.multi && <small> · เลือกได้หลายข้อ</small>}</legend>
                <div className="mo-join-chips" role="group" aria-label={q.label}>
                  {q.options.map((o) => {
                    const on = (answers[q.id] || []).includes(o)
                    return <button key={o} type="button" aria-pressed={on} className="mo-join-chip" onClick={() => setAnswers(toggleAnswer(answers, q, o))}>{o}</button>
                  })}
                </div>
                {q.sensitive && <label className="mo-profile-confirm mo-join-consent"><input type="checkbox" checked={beliefConsent} onChange={(event) => setBeliefConsent(event.target.checked)} /><span>ยินยอมให้ใช้ข้อมูลข้อนี้ เพื่อแนะนำพิธีและกิจกรรมที่ตรงใจ<small>ไม่บังคับ · ไม่ติ๊ก = ระบบไม่บันทึกคำตอบข้อนี้</small></span></label>}
              </fieldset>
            ))}
            {bonusOpen && <div className="mo-join-reward" aria-live="polite"><span>ตอบครบ {progress.done}/{progress.total} รับ <b>+{surveyConfig!.bonusPoints} แต้ม</b></span><span className="mo-join-meter"><i style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} /></span></div>}
          </>}
          <div className="mo-join-privacy">
            <b>ข้อมูลของคุณ เราดูแลอย่างไร</b>
            <span>ใช้เพื่อจัดส่งรายการบูชา ดูแลสมาชิก และเชิญร่วมกิจกรรม/สิทธิพิเศษที่ตรงใจเท่านั้น · ไม่เปิดเผย ไม่ขาย ไม่ส่งต่อให้บุคคลภายนอก · ขอดู แก้ไข หรือให้ลบได้ทุกเมื่อ ทักแอดมินในแชท LINE</span>
          </div>
          <label className="mo-profile-confirm"><input required type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>ยืนยันว่าข้อมูลนี้เป็นข้อมูลปัจจุบันของฉัน</span></label>
          <p className="mo-address-intro">ข้อมูลนี้ใช้เติมในออร์เดอร์ใหม่เท่านั้น การแก้ไขจะไม่เปลี่ยนออร์เดอร์เดิม</p>
          <button type="submit" className="mo-address-primary" disabled={loading}>{loading ? 'กำลังบันทึก...' : joining ? 'สมัครสมาชิก' : 'ยืนยันและบันทึกข้อมูล'}</button>
        </form>}
      </section>
    </div>, document.body)}
  </>
}
